import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtTL, fmtInt } from "@/lib/format";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar,
  XAxis, YAxis, CartesianGrid,
} from "recharts";
import { StatusBadge } from "@/components/StatusBadge";

const COLORS = ["#0B3676", "#16A34A", "#F59E0B", "#DC2626", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16"];

export default function StockReports() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/stock-summary").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-[#6B7280]">Yükleniyor...</div>;

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-3 gap-4">
        <div className="azd-card azd-stat-card p-5">
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Toplam Ürün</div>
          <div className="text-2xl font-extrabold text-[#08285A] mt-1">{fmtInt(data.total_products)}</div>
        </div>
        <div className="azd-card azd-stat-card azd-accent p-5">
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Toplam Stok Değeri</div>
          <div className="text-2xl font-extrabold text-[#08285A] mt-1">{fmtTL(data.total_value)}</div>
        </div>
        <div className="azd-card azd-stat-card azd-warning p-5">
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Kritik / Stoksuz</div>
          <div className="text-2xl font-extrabold text-[#F59E0B] mt-1">
            {fmtInt(data.critical_items.length)} / <span className="text-[#DC2626]">{fmtInt(data.out_of_stock.length)}</span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="azd-card p-5">
          <div className="text-sm font-bold text-[#08285A] mb-3 uppercase tracking-wider">Kategoriye Göre</div>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.by_category} dataKey="value" nameKey="name" outerRadius={85}>
                  {data.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtTL(v)} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="azd-card p-5">
          <div className="text-sm font-bold text-[#08285A] mb-3 uppercase tracking-wider">Markaya Göre</div>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.by_brand} dataKey="value" nameKey="name" innerRadius={45} outerRadius={85}>
                  {data.by_brand.map((_, i) => <Cell key={i} fill={COLORS[(i+2) % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtTL(v)} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="azd-card p-5">
          <div className="text-sm font-bold text-[#08285A] mb-3 uppercase tracking-wider">Kaynağa Göre</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.by_source}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                <Tooltip formatter={(v) => fmtTL(v)} />
                <Bar dataKey="value" fill="#0B3676" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="azd-card p-5">
        <div className="text-sm font-bold text-[#08285A] mb-3 uppercase tracking-wider">
          Kritik & Stoksuz Ürünler ({data.critical_items.length + data.out_of_stock.length})
        </div>
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Kod</th>
                <th className="text-left px-3 py-2 font-semibold">Ürün</th>
                <th className="text-right px-3 py-2 font-semibold">Mevcut</th>
                <th className="text-right px-3 py-2 font-semibold">Kritik</th>
                <th className="text-center px-3 py-2 font-semibold">Durum</th>
              </tr>
            </thead>
            <tbody>
              {[...data.out_of_stock, ...data.critical_items].map((p) => (
                <tr key={p.id} className="border-t border-[#E5E7EB]">
                  <td className="px-3 py-2 font-mono text-xs text-[#0B3676]">{p.product_code}</td>
                  <td className="px-3 py-2 font-semibold">{p.product_name}</td>
                  <td className="px-3 py-2 text-right font-bold">{fmtInt(p.current_stock)} {p.unit}</td>
                  <td className="px-3 py-2 text-right text-xs text-[#6B7280]">{fmtInt(p.critical_stock_level)} {p.unit}</td>
                  <td className="px-3 py-2 text-center">
                    <StatusBadge status={Number(p.current_stock) <= 0 ? "out" : "critical"} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
